<script setup lang="ts">
import { router, usePage } from '@inertiajs/vue3'
import { Copy, Eye, KeyRound, Plus, Trash2 } from 'lucide-vue-next'
import { ref, watch } from 'vue'

import AppDialog from '@/components/AppDialog.vue'
import InputError from '@/components/InputError.vue'
import RelativeTime from '@/components/RelativeTime.vue'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useCopyToClipboard } from '@/composables/useCopyToClipboard'
import { useDialog } from '@/composables/useDialog'

interface Token {
  id: number
  name: string
  access: 'read' | 'read_write'
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

interface NewToken {
  id: number
  name: string
  raw: string
}

const props = defineProps<{
  tokens: Token[]
  createPath: string
  destroyPath: (id: number) => string
}>()

const page = usePage()
const { copy, isCopied } = useCopyToClipboard()

const EXPIRY_OPTIONS = [
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
  { label: '1 year', value: '365' },
  { label: 'No expiry', value: 'none' },
]

const showCreateDialog = ref(false)
const showRawDialog = ref(false)
const newTokenName = ref('')
const newTokenExpiry = ref('90')
const newTokenReadOnly = ref(false)
const newTokenRaw = ref<NewToken | null>(null)
const createError = ref<string | null>(null)
const processing = ref(false)

// Detect newly created token returned via inertia flash
watch(
  () => (page.flash as Record<string, unknown> | undefined)?.new_token as NewToken | undefined,
  (token) => {
    if (token) {
      newTokenRaw.value = token
      showRawDialog.value = true
    }
  },
  { immediate: true },
)

function openCreate() {
  newTokenName.value = ''
  newTokenExpiry.value = '90'
  newTokenReadOnly.value = false
  createError.value = null
  showCreateDialog.value = true
}

function submitCreate() {
  if (!newTokenName.value.trim()) {
    createError.value = 'Name is required'
    return
  }
  processing.value = true
  const expiresAt =
    newTokenExpiry.value === 'none'
      ? null
      : new Date(Date.now() + Number(newTokenExpiry.value) * 86400000).toISOString()
  router.post(
    props.createPath,
    {
      name: newTokenName.value,
      expires_at: expiresAt,
      access: newTokenReadOnly.value ? 'read' : 'read_write',
    },
    {
      onSuccess: () => {
        showCreateDialog.value = false
      },
      onFinish: () => {
        processing.value = false
      },
    },
  )
}

function copyRaw() {
  if (!newTokenRaw.value) return
  copy(newTokenRaw.value.raw, newTokenRaw.value.id)
}

function closeRawDialog() {
  newTokenRaw.value = null
  showRawDialog.value = false
}

const { confirm } = useDialog()

async function confirmRevoke(token: Token) {
  const ok = await confirm({
    title: 'Revoke token?',
    description: `Revoke ${token.name}? Any app using it will lose access immediately.`,
    confirmLabel: 'Revoke',
  })
  if (!ok) return
  router.delete(props.destroyPath(token.id))
}
</script>

<template>
  <Card>
    <CardHeader>
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <KeyRound class="text-muted-foreground size-5" />
          <div>
            <CardTitle class="text-base">API Tokens</CardTitle>
            <CardDescription>Manage tokens for programmatic access</CardDescription>
          </div>
        </div>
        <Button size="sm" @click="openCreate">
          <Plus class="size-4" />
          New Token
        </Button>
      </div>
    </CardHeader>
    <CardContent class="p-0">
      <!-- Empty -->
      <div v-if="tokens.length === 0" class="flex flex-col items-center gap-2 py-12 text-center">
        <KeyRound class="text-muted-foreground/40 size-10" />
        <p class="text-muted-foreground text-sm">No tokens yet</p>
      </div>

      <!-- List -->
      <div v-else class="divide-border divide-y">
        <div v-for="token in tokens" :key="token.id" class="flex items-center justify-between px-6 py-4">
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <p class="text-sm font-medium">{{ token.name }}</p>
              <Badge v-if="token.access === 'read'" variant="secondary" class="text-xs">Read only</Badge>
              <Badge v-else variant="outline" class="text-xs">Read & Write</Badge>
            </div>
            <p class="text-muted-foreground text-xs">
              Created <RelativeTime :date="token.created_at" /> · Last used
              <span v-if="token.last_used_at"><RelativeTime :date="token.last_used_at" /></span
              ><span v-else>Never</span>
              <span v-if="token.expires_at"> · Expires <RelativeTime :date="token.expires_at" /></span>
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            class="text-destructive hover:text-destructive size-8"
            @click="confirmRevoke(token)"
          >
            <Trash2 class="size-4" />
            <span class="sr-only">Revoke</span>
          </Button>
        </div>
      </div>
    </CardContent>
  </Card>

  <!-- Create dialog -->
  <AppDialog
    v-model:open="showCreateDialog"
    title="New API Token"
    description="Give your token a descriptive name."
    :loading="processing"
    loading-message="Creating token…"
  >
    <div class="space-y-4">
      <div class="grid gap-2">
        <Label for="token-name">Token Name</Label>
        <Input id="token-name" v-model="newTokenName" placeholder="e.g. Production server" autofocus />
        <InputError v-if="createError" :messages="[createError]" />
      </div>
      <div class="grid gap-2">
        <Label>Expiry</Label>
        <Select v-model="newTokenExpiry">
          <SelectTrigger class="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="opt in EXPIRY_OPTIONS" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div class="grid gap-2">
        <Label>Permission</Label>
        <div class="flex items-center justify-between rounded-lg border px-4 py-3">
          <div>
            <Label for="token-readonly" class="cursor-pointer font-medium">
              {{ newTokenReadOnly ? 'Read only' : 'Read & Write' }}
            </Label>
            <p class="text-muted-foreground text-xs">
              {{ newTokenReadOnly ? 'Cannot create or update records' : 'Can create and update records' }}
            </p>
          </div>
          <Switch id="token-readonly" v-model="newTokenReadOnly" />
        </div>
      </div>
    </div>
    <template #footer>
      <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
      <Button :disabled="processing" @click="submitCreate">Create Token</Button>
    </template>
  </AppDialog>

  <!-- Raw token reveal dialog -->
  <Dialog v-model:open="showRawDialog">
    <DialogContent class="sm:max-w-md [&>button:last-child]:hidden" @interact-outside.prevent @escape-key-down.prevent>
      <DialogHeader>
        <DialogTitle>Token Created</DialogTitle>
        <DialogDescription> Copy this token now. It will <strong>not</strong> be shown again. </DialogDescription>
      </DialogHeader>
      <div class="space-y-3">
        <div class="bg-muted flex items-center gap-2 rounded-md px-3 py-2">
          <Eye class="text-muted-foreground size-4 shrink-0" />
          <code class="flex-1 text-xs break-all">{{ newTokenRaw?.raw }}</code>
          <Button variant="ghost" size="icon" class="size-7 shrink-0" @click="copyRaw">
            <Copy class="size-3.5" />
          </Button>
        </div>
        <p v-if="newTokenRaw && isCopied(newTokenRaw.id)" class="text-muted-foreground text-center text-xs">Copied!</p>
      </div>
      <DialogFooter>
        <Button @click="closeRawDialog"> Done </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
